// src/pages/StaffDashboard.jsx
import React, { useEffect, useState } from 'react';
import Sidebar from '../components/shared/Sidebar';
import { useAuth } from '../hooks/useAuth';
import { subscribeMyTasks, subscribeShipments, subscribeConsignees } from '../firebase/firestore';

const STATUS_CLASS = {
  Pending: 'pending', 'In Transit': 'transit', Arrived: 'arrived',
  'Customs Clearance': 'customs', Cleared: 'cleared',
  Delivered: 'delivered', 'On Hold': 'held',
};

const STATUS_COLOR = {
  Pending: 'var(--gray-500)', 'In Transit': 'var(--blue)', Arrived: '#639922',
  'Customs Clearance': 'var(--amber)', Cleared: 'var(--teal)',
  Delivered: 'var(--green)', 'On Hold': 'var(--red)',
};

function isOverdue(s) {
  if (!s.eta || s.status === 'Delivered' || s.status === 'Cleared') return false;
  return new Date(s.eta) < new Date(new Date().toDateString());
}

export default function StaffDashboard() {
  const { profile } = useAuth();
  const [myTasks,    setMyTasks]    = useState([]);
  const [shipments,  setShipments]  = useState([]);
  const [consignees, setConsignees] = useState([]);

  useEffect(() => {
    if (!profile?.uid) return;
    const u1 = subscribeMyTasks(profile.uid, setMyTasks);
    const u2 = subscribeShipments(setShipments);
    const u3 = subscribeConsignees(setConsignees);
    return () => { u1(); u2(); u3(); };
  }, [profile?.uid]);

  const getCons = (id) => consignees.find(c => c.id === id) || { name: '—' };

  // Task metrics
  const openTasks    = myTasks.filter(t => t.status !== 'done').length;
  const doneTasks    = myTasks.filter(t => t.status === 'done').length;
  const urgentTasks  = myTasks.filter(t => t.status === 'urgent').length;

  // Shipment metrics
  const shipCounts = {};
  shipments.forEach(s => { shipCounts[s.status] = (shipCounts[s.status] || 0) + 1; });
  const maxShipCount  = Math.max(...Object.values(shipCounts), 1);
  const overdueList   = shipments.filter(isOverdue);
  const attentionList = shipments.filter(s => s.status === 'On Hold' || isOverdue(s));

  const recentShipments = [...shipments]
    .sort((a, b) => {
      const at = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
      const bt = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
      return bt - at;
    })
    .slice(0, 5);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="shell">
      <Sidebar />
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">{greeting()}, {profile?.name?.split(' ')[0] || 'there'}</div>
            <div className="page-sub">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long' })}
              {' · '}You have {openTasks} open task{openTasks !== 1 ? 's' : ''}
              {attentionList.length > 0 && ` · ${attentionList.length} shipment${attentionList.length !== 1 ? 's' : ''} need attention`}
            </div>
          </div>
        </div>

        <div className="scroll">

          {/* Alerts */}
          {(shipCounts['On Hold'] > 0 || overdueList.length > 0) && (
            <div style={{ marginBottom: 16 }}>
              {shipCounts['On Hold'] > 0 && (
                <div className="alert warning">
                  <i className="ti ti-alert-triangle" />
                  <div><strong>{shipCounts['On Hold']} shipment{shipCounts['On Hold'] > 1 ? 's' : ''} on hold</strong> — check with agent or supplier for resolution.</div>
                </div>
              )}
              {overdueList.length > 0 && (
                <div className="alert danger">
                  <i className="ti ti-clock" />
                  <div><strong>{overdueList.length} shipment{overdueList.length > 1 ? 's' : ''} past ETA</strong> without being cleared or delivered. Update status or follow up.</div>
                </div>
              )}
            </div>
          )}

          {/* Task metrics */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>My tasks</div>
          <div className="metrics-grid" style={{ marginBottom: 20 }}>
            <div className="metric blue"><div className="metric-num">{myTasks.length}</div><div className="metric-label">Total assigned</div></div>
            <div className="metric amber"><div className="metric-num">{openTasks}</div><div className="metric-label">Open</div></div>
            <div className="metric red"><div className="metric-num">{urgentTasks}</div><div className="metric-label">Urgent</div></div>
            <div className="metric green"><div className="metric-num">{doneTasks}</div><div className="metric-label">Done</div></div>
          </div>

          {/* Shipment metrics */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Shipments</div>
          <div className="metrics-grid" style={{ marginBottom: 20 }}>
            <div className="metric blue"><div className="metric-num">{shipments.length}</div><div className="metric-label">Total</div></div>
            <div className="metric blue"><div className="metric-num">{shipCounts['In Transit'] || 0}</div><div className="metric-label">In transit</div></div>
            <div className="metric amber"><div className="metric-num">{shipCounts['Customs Clearance'] || 0}</div><div className="metric-label">At customs</div></div>
            <div className="metric red"><div className="metric-num">{shipCounts['On Hold'] || 0}</div><div className="metric-label">On hold</div></div>
            <div className="metric teal"><div className="metric-num">{shipCounts['Cleared'] || 0}</div><div className="metric-label">Cleared</div></div>
            <div className="metric green"><div className="metric-num">{shipCounts['Delivered'] || 0}</div><div className="metric-label">Delivered</div></div>
          </div>

          <div className="grid-2">
            {/* My tasks */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><i className="ti ti-user-check" style={{ color: 'var(--blue)' }} /> My assigned tasks</span>
              </div>
              {myTasks.length === 0
                ? <div className="empty-state"><i className="ti ti-checklist" /><p>No tasks assigned yet.</p></div>
                : myTasks.slice(0, 6).map(t => (
                  <div key={t.id} style={{ borderRadius: '0 8px 8px 0', border: '1px solid var(--gray-200)', borderLeft: `3px solid ${t.status === 'urgent' ? 'var(--red)' : t.status === 'done' ? 'var(--green)' : 'var(--blue)'}`, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.title}</div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span className={`badge ${t.status === 'done' ? 'done' : t.status === 'urgent' ? 'urgent' : 'progress'}`}>{t.status}</span>
                      {t.dueDate && <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Due {t.dueDate}</span>}
                    </div>
                  </div>
                ))}
            </div>

            {/* Recent shipments */}
            <div className="card">
              <div className="card-header">
                <span className="card-title"><i className="ti ti-ship" style={{ color: 'var(--blue)' }} /> Recent shipments</span>
              </div>
              {recentShipments.length === 0
                ? <div className="empty-state"><i className="ti ti-ship" /><p>No shipments yet.</p></div>
                : recentShipments.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: '1px solid var(--gray-200)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue)' }}>{s.bl}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{getCons(s.consigneeId).name} · ETA {s.eta || '—'}</div>
                    </div>
                    <span className={`badge ${STATUS_CLASS[s.status] || 'pending'}`}>{s.status}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Status breakdown */}
          <div className="card">
            <div className="card-header">
              <span className="card-title"><i className="ti ti-chart-donut" style={{ color: 'var(--purple)' }} /> Status breakdown</span>
            </div>
            {Object.keys(shipCounts).length === 0
              ? <div className="empty-state"><i className="ti ti-chart-donut" /><p>No shipments yet.</p></div>
              : Object.entries(shipCounts).map(([st, n]) => (
                <div key={st} className="bar-row">
                  <div className="bar-meta"><span>{st}</span><span style={{ fontWeight: 600 }}>{n}</span></div>
                  <div className="bar-bg">
                    <div className="bar-fill" style={{ width: `${Math.round(n / maxShipCount * 100)}%`, background: STATUS_COLOR[st] || 'var(--gray-500)' }} />
                  </div>
                </div>
              ))}
          </div>

          {/* Attention table */}
          {attentionList.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title"><i className="ti ti-alert-triangle" style={{ color: 'var(--amber)' }} /> Requires attention</span>
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>On hold or overdue ETA</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>BL / AWB</th><th>Consignee</th><th>Status</th><th>ETA</th><th>Remarks</th></tr></thead>
                  <tbody>{attentionList.map(s => (
                    <tr key={s.id}>
                      <td><strong style={{ color: 'var(--blue)' }}>{s.bl}</strong></td>
                      <td>{getCons(s.consigneeId).name}</td>
                      <td><span className={`badge ${STATUS_CLASS[s.status] || 'pending'}`}>{s.status}</span></td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{s.eta || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{s.remarks || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
